import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
  type WriteBatch,
} from "firebase/firestore";
import { firestore } from "./firebase_config";
import { Constants } from "../constant";
import type {
  HappeningNowDoc,
  HappeningNowListItem,
} from "../types/happeningNow";
import type {
  CarouselCardSnapshot,
  CarouselPublicationDoc,
  CarouselPublicationListItem,
} from "../types/carouselPublication";
import { listHappeningNowItems } from "./happeningNowService";
import { normalizeHappeningNowPlatforms } from "./happeningNowPlatforms";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";

const PUBLICATION_ID_PATTERN = /^[a-z0-9]{1,32}$/;
const CARD_ID_PATTERN = /^[a-z0-9]{1,32}$/;

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

function randomId(length = 14): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[bytes[i]! % chars.length];
  }
  return out;
}

async function allocatePublicationId(): Promise<string> {
  for (let attempt = 0; attempt < 12; attempt++) {
    const candidate = randomId(14);
    if (!PUBLICATION_ID_PATTERN.test(candidate)) continue;
    const snap = await getDoc(
      doc(firestore, Constants.CAROUSEL_PUBLICATIONS_COLLECTION, candidate),
    );
    if (!snap.exists()) return candidate;
  }
  throw new Error("Could not allocate a publication id. Try again.");
}

/**
 * Card ids on the draft must not collide with any existing draft, live, or
 * historical card id (we never re-use ids — clicks are attributed per-instance).
 */
async function allocateDraftCardId(
  takenIds: ReadonlySet<string>,
): Promise<string> {
  for (let attempt = 0; attempt < 24; attempt++) {
    const candidate = randomId(12);
    if (!CARD_ID_PATTERN.test(candidate)) continue;
    if (takenIds.has(candidate)) continue;
    const [draftSnap, liveSnap] = await Promise.all([
      getDoc(
        doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, candidate),
      ),
      getDoc(doc(firestore, Constants.HAPPENING_NOW_COLLECTION, candidate)),
    ]);
    if (!draftSnap.exists() && !liveSnap.exists()) return candidate;
  }
  throw new Error("Could not allocate a card id. Try again.");
}

function freezeCard(card: HappeningNowDoc): CarouselCardSnapshot {
  const out: CarouselCardSnapshot = {
    id: card.id,
    title: card.title,
    imageUrl: card.imageUrl,
    imagePath: card.imagePath,
    targetLink: card.targetLink,
    expiresAt: card.expiresAt,
    active: card.active,
    order: card.order,
    createdBy: card.createdBy,
    createdByDisplayName: card.createdByDisplayName,
  };
  if (card.startsAt) out.startsAt = card.startsAt;
  if (card.description) out.description = card.description;
  if (card.imageWidth) out.imageWidth = card.imageWidth;
  if (card.imageHeight) out.imageHeight = card.imageHeight;
  out.platforms = normalizeHappeningNowPlatforms(card.platforms);
  return out;
}

/**
 * Stable hash of the carousel content used by the status pill to decide
 * "in sync" vs "unpublished changes". Compares the same fields a publish
 * would freeze, plus the draft's order. NOT a security boundary — purely UX.
 */
export function carouselContentHash(cards: CarouselCardSnapshot[]): string {
  const tuples = cards
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((c) =>
      [
        c.id,
        c.title,
        c.description ?? "",
        c.imageUrl,
        c.targetLink,
        c.startsAt?.toMillis() ?? 0,
        c.expiresAt.toMillis(),
        c.active ? 1 : 0,
        c.order,
        normalizeHappeningNowPlatforms(c.platforms).join(","),
      ].join("|"),
    );
  return tuples.join("∎");
}

export function draftToSnapshotCards(
  drafts: HappeningNowListItem[],
): CarouselCardSnapshot[] {
  return drafts.map(freezeCard);
}

export function liveToSnapshotCards(
  live: HappeningNowListItem[],
): CarouselCardSnapshot[] {
  return live.map(freezeCard);
}

/** Strip undefined keys so Firestore doesn't choke. */
function compact<T extends Record<string, unknown>>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o)) {
    if (o[k] !== undefined) out[k] = o[k];
  }
  return out as T;
}

// ---------------------------------------------------------------------------
// PUBLISH
// ---------------------------------------------------------------------------

export interface PublishCarouselResult {
  publicationId: string;
  cardCount: number;
}

/**
 * Snapshot the working draft and atomically replace the live carousel with it.
 *
 * Behavior:
 *   - Reads the current draft + live state.
 *   - Writes a `carouselPublications/{pubId}` doc with frozen card data.
 *   - For each draft card, upserts a matching `happeningNow/live/items/{id}` doc.
 *     - Each card written by this publish gets `clickCount: 0` and
 *       `lastClickedAt: null` so the live carousel reflects fresh tap counts for
 *       this publication (existing live ids included).
 *   - Deletes any live docs whose id is no longer in the draft.
 *
 * All Firestore writes go through a single batch — partial publishes can't
 * happen unless the network is severed mid-commit.
 */
export async function publishHappeningNowCarousel(
  uid: string,
  actor?: ActivityActor | null,
): Promise<PublishCarouselResult> {
  const drafts = await listHappeningNowItems();
  if (drafts.length === 0) {
    throw new Error(
      "Draft is empty — add at least one card before publishing.",
    );
  }

  const liveSnap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_COLLECTION)),
  );
  const liveById = new Map<string, HappeningNowDoc>();
  liveSnap.forEach((d) => {
    const data = d.data() as HappeningNowDoc;
    liveById.set(d.id, data);
  });

  const publicationId = await allocatePublicationId();
  const cards = drafts.map(freezeCard);
  const imagePathsUsed = Array.from(
    new Set(cards.map((c) => c.imagePath).filter(Boolean)),
  );

  const batch = writeBatch(firestore);

  const pubRef = doc(
    firestore,
    Constants.CAROUSEL_PUBLICATIONS_COLLECTION,
    publicationId,
  );
  batch.set(pubRef, {
    id: publicationId,
    publishedAt: serverTimestamp(),
    publishedBy: uid,
    publishedByDisplayName: actor?.displayName?.trim() || actor?.email || uid,
    cardCount: cards.length,
    cards,
    imagePathsUsed,
  });

  const draftIds = new Set(drafts.map((c) => c.id));
  for (const card of drafts) {
    const liveRef = doc(firestore, Constants.HAPPENING_NOW_COLLECTION, card.id);
    const existing = liveById.get(card.id);
    // Re-publishes preserve the original live `createdBy` / `createdAt` (the
    // Firestore rule for `update` checks both are unchanged). Brand-new cards
    // inherit the draft creator.
    const payload: Record<string, unknown> = compact({
      id: card.id,
      title: card.title,
      imageUrl: card.imageUrl,
      imagePath: card.imagePath,
      imageWidth: card.imageWidth ?? undefined,
      imageHeight: card.imageHeight ?? undefined,
      description: card.description ?? undefined,
      targetLink: card.targetLink,
      startsAt: card.startsAt ?? undefined,
      expiresAt: card.expiresAt,
      active: card.active,
      order: card.order,
      platforms: normalizeHappeningNowPlatforms(card.platforms),
      clickCount: 0,
      lastClickedAt: null,
      createdBy: existing?.createdBy ?? card.createdBy,
      createdByDisplayName:
        existing?.createdByDisplayName ?? card.createdByDisplayName,
      createdAt: existing?.createdAt ?? card.createdAt ?? serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.set(liveRef, payload);
  }

  liveById.forEach((_value, liveId) => {
    if (!draftIds.has(liveId)) {
      batch.delete(doc(firestore, Constants.HAPPENING_NOW_COLLECTION, liveId));
    }
  });

  // Clear the draft as part of the same atomic batch — operators always start
  // a fresh draft after publishing. If the publish commit fails, the draft
  // stays intact.
  for (const card of drafts) {
    batch.delete(
      doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, card.id),
    );
  }

  await batch.commit();

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "publish",
      resourceType: "carousel_publication",
      resourceId: publicationId,
      summary: `Published carousel (${cards.length} ${
        cards.length === 1 ? "card" : "cards"
      })`,
      actor,
      details: { cardCount: cards.length },
    });
  }

  return { publicationId, cardCount: cards.length };
}

// ---------------------------------------------------------------------------
// LIST / PREVIEW
// ---------------------------------------------------------------------------

/** Newest publications first, optionally filtered to the last N days. */
export async function listCarouselPublications(options?: {
  sinceDays?: number;
  starredOnly?: boolean;
}): Promise<CarouselPublicationListItem[]> {
  const sinceDays = options?.sinceDays;
  const starredOnly = options?.starredOnly ?? false;

  const colRef = collection(
    firestore,
    Constants.CAROUSEL_PUBLICATIONS_COLLECTION,
  );
  // Firestore can't combine `where starredAt != null` with another inequality,
  // so we filter in memory — typical history sizes are well under 1k docs.
  const snap = await getDocs(
    sinceDays && !starredOnly
      ? query(
          colRef,
          where(
            "publishedAt",
            ">=",
            Timestamp.fromMillis(Date.now() - sinceDays * 86_400_000),
          ),
        )
      : query(colRef),
  );
  const rows: CarouselPublicationListItem[] = [];
  snap.forEach((d) => {
    const data = d.data() as CarouselPublicationDoc;
    rows.push(data);
  });

  let filtered = rows;
  if (starredOnly) {
    filtered = filtered.filter((r) => Boolean(r.starredAt));
  } else if (sinceDays) {
    // Always keep starred publications visible — they're explicit operator
    // intent to retain regardless of age.
    const cutoff = Date.now() - sinceDays * 86_400_000;
    filtered = filtered.filter(
      (r) => r.publishedAt.toMillis() >= cutoff || Boolean(r.starredAt),
    );
  }
  filtered.sort((a, b) => b.publishedAt.toMillis() - a.publishedAt.toMillis());
  return filtered;
}

export async function getCarouselPublication(
  id: string,
): Promise<CarouselPublicationDoc | null> {
  const snap = await getDoc(
    doc(firestore, Constants.CAROUSEL_PUBLICATIONS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return snap.data() as CarouselPublicationDoc;
}

// ---------------------------------------------------------------------------
// STAR / UNSTAR (favorite — excluded from auto-cleanup)
// ---------------------------------------------------------------------------

export async function setCarouselPublicationStarred(
  id: string,
  starred: boolean,
  uid: string,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.CAROUSEL_PUBLICATIONS_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Publication not found.");
  if (starred) {
    await updateDoc(ref, {
      starredAt: serverTimestamp(),
      starredBy: uid,
      starredByDisplayName: actor?.displayName?.trim() || actor?.email || uid,
    });
  } else {
    await updateDoc(ref, {
      starredAt: deleteField(),
      starredBy: deleteField(),
      starredByDisplayName: deleteField(),
    });
  }
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: starred ? "star_publication" : "unstar_publication",
      resourceType: "carousel_publication",
      resourceId: id,
      summary: starred
        ? "Starred a past carousel (excluded from auto-cleanup)"
        : "Unstarred a past carousel",
      actor,
    });
  }
}

// ---------------------------------------------------------------------------
// REPUBLISH (into draft, then operator publishes manually)
// ---------------------------------------------------------------------------

export interface RepublishOptions {
  /** Subset of `publication.cards.id` values to restore (defaults to all). */
  includeCardIds?: string[];
  /**
   * If set, every restored card gets this expiresAt. Useful because original
   * snapshot expiry is almost always in the past.
   */
  newExpiresAt?: Timestamp | null;
  /**
   * 'replace' (default): clear current draft before restoring. 'append':
   * keep current draft cards (restored ones go after).
   */
  mode?: "replace" | "append";
}

export interface RepublishResult {
  publicationId: string;
  restoredCount: number;
  skippedCount: number;
  newDraftCardIds: string[];
}

/**
 * Restore (a subset of) a past publication into the working draft. Each
 * restored card gets a *fresh* id so click metrics on the next publish start
 * from zero — campaign-instance attribution.
 */
export async function republishCarouselToDraft(
  publicationId: string,
  options: RepublishOptions,
  uid: string,
  actor?: ActivityActor | null,
): Promise<RepublishResult> {
  const pub = await getCarouselPublication(publicationId);
  if (!pub) throw new Error("Publication not found.");

  const includeSet = options.includeCardIds
    ? new Set(options.includeCardIds)
    : new Set(pub.cards.map((c) => c.id));

  const restored = pub.cards
    .filter((c) => includeSet.has(c.id))
    .sort((a, b) => a.order - b.order);
  const skipped = pub.cards.length - restored.length;

  if (restored.length === 0) {
    throw new Error("Nothing to restore — pick at least one card.");
  }

  const mode = options.mode ?? "replace";
  const newExpires = options.newExpiresAt ?? null;

  // Sequentially mint fresh ids; allocateDraftCardId hits Firestore so we keep
  // a local set to avoid intra-batch collisions on the same tick.
  const minted: string[] = [];
  const used = new Set<string>();
  for (let i = 0; i < restored.length; i++) {
    const id = await allocateDraftCardId(used);
    used.add(id);
    minted.push(id);
  }

  const batch = writeBatch(firestore);

  if (mode === "replace") {
    const draftSnap = await getDocs(
      query(collection(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION)),
    );
    draftSnap.forEach((d) => {
      batch.delete(d.ref);
    });
  }

  // For append mode we want restored cards to go after existing draft cards.
  let baseOrder = 0;
  if (mode === "append") {
    const draftSnap = await getDocs(
      query(collection(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION)),
    );
    draftSnap.forEach((d) => {
      const v = (d.data() as HappeningNowDoc).order;
      if (typeof v === "number" && v > baseOrder) baseOrder = v;
    });
  }

  restored.forEach((card, idx) => {
    const newId = minted[idx]!;
    const ref: DocumentReference = doc(
      firestore,
      Constants.HAPPENING_NOW_DRAFT_COLLECTION,
      newId,
    );
    const payload: Record<string, unknown> = compact({
      id: newId,
      title: card.title,
      description: card.description ?? undefined,
      imageUrl: card.imageUrl,
      imagePath: card.imagePath,
      imageWidth: card.imageWidth ?? undefined,
      imageHeight: card.imageHeight ?? undefined,
      targetLink: card.targetLink,
      // Restored cards start showing immediately — the operator picks a fresh
      // expiry window and can re-schedule a start via edit if they want one.
      expiresAt: newExpires ?? card.expiresAt,
      active: card.active,
      order: baseOrder + (idx + 1) * 10,
      platforms: normalizeHappeningNowPlatforms(card.platforms),
      createdBy: uid,
      createdByDisplayName: actor?.displayName?.trim() || actor?.email || uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Provenance — handy for debugging and the "Restored from" badge later.
      restoredFromPublicationId: publicationId,
      restoredFromCardId: card.id,
    });
    batch.set(ref, payload);
  });

  await batch.commit();

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "republish_to_draft",
      resourceType: "carousel_publication",
      resourceId: publicationId,
      summary: `Restored ${restored.length} ${
        restored.length === 1 ? "card" : "cards"
      } to draft from a past carousel`,
      actor,
      details: {
        mode,
        restored: restored.length,
        skipped,
        newCardIds: minted,
      },
    });
  }

  return {
    publicationId,
    restoredCount: restored.length,
    skippedCount: skipped,
    newDraftCardIds: minted,
  };
}

// ---------------------------------------------------------------------------
// SEED DRAFT FROM LIVE  (one-shot helper for first-time users)
// ---------------------------------------------------------------------------

/**
 * Copy the current live carousel into the draft. Intended for one-time use
 * when the operator first sees the new draft/publish flow with an empty draft
 * but populated live carousel. Idempotent: if the same id already exists in
 * the draft it's overwritten.
 */
export async function seedDraftFromLive(
  uid: string,
  actor?: ActivityActor | null,
): Promise<{ count: number }> {
  const liveSnap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_COLLECTION)),
  );
  if (liveSnap.empty) return { count: 0 };

  const displayName = actor?.displayName?.trim() || actor?.email || uid;

  const batch: WriteBatch = writeBatch(firestore);
  let count = 0;
  liveSnap.forEach((d) => {
    const data = d.data() as HappeningNowDoc;
    const ref = doc(
      firestore,
      Constants.HAPPENING_NOW_DRAFT_COLLECTION,
      data.id,
    );
    // The draft-create rule requires `createdBy == request.auth.uid` and
    // `createdAt == request.time` — these are fresh draft entries authored by
    // the current operator, not a 1:1 copy of the live doc.
    batch.set(
      ref,
      compact({
        id: data.id,
        title: data.title,
        description: data.description ?? undefined,
        imageUrl: data.imageUrl,
        imagePath: data.imagePath,
        imageWidth: data.imageWidth ?? undefined,
        imageHeight: data.imageHeight ?? undefined,
        targetLink: data.targetLink,
        startsAt: data.startsAt ?? undefined,
        expiresAt: data.expiresAt,
        active: data.active,
        order: data.order,
        platforms: normalizeHappeningNowPlatforms(data.platforms),
        createdBy: uid,
        createdByDisplayName: displayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    );
    count += 1;
  });
  await batch.commit();
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "seed_draft_from_live",
      resourceType: "happening_now_item",
      resourceId: "draft",
      summary: `Seeded draft from live carousel (${count} ${
        count === 1 ? "card" : "cards"
      })`,
      actor,
    });
  }
  return { count };
}

// ---------------------------------------------------------------------------
// CLEAR DRAFT (operator-initiated reset)
// ---------------------------------------------------------------------------

export async function clearDraft(
  actor?: ActivityActor | null,
): Promise<{ count: number }> {
  const snap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION)),
  );
  if (snap.empty) return { count: 0 };
  const batch = writeBatch(firestore);
  snap.forEach((d) => batch.delete(d.ref));
  await batch.commit();
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "clear_draft",
      resourceType: "happening_now_item",
      resourceId: "draft",
      summary: `Cleared draft (${snap.size} removed)`,
      actor,
    });
  }
  return { count: snap.size };
}

// ---------------------------------------------------------------------------
// DELETE PUBLICATION (operator-initiated, e.g. mistake)
// ---------------------------------------------------------------------------

export async function deleteCarouselPublication(
  id: string,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.CAROUSEL_PUBLICATIONS_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Publication not found.");
  await deleteDoc(ref);
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "delete_publication",
      resourceType: "carousel_publication",
      resourceId: id,
      summary: "Deleted a past carousel from history",
      actor,
    });
  }
}
